import { createRoot, hydrateRoot } from 'react-dom/client';
import { buildTree } from './tree';
import { Site } from './shared/lib/types';
import { loadIcons } from './icons';
import { watchAppleDataDetectors } from './shared/lib/errors/appleDataDetectors';
import { initErrorReporting, reportError } from './shared/lib/errors/reportError';
import './styles/v2.css';

function readSite(): Site {
  const el = document.getElementById('dm-v2-site');

  if (!el || !el.textContent) {
    throw new Error('Missing #dm-v2-site payload — is the V2 layout in use?');
  }

  return JSON.parse(el.textContent) as Site;
}

// React adds #423 after the error that actually broke hydration; that error is already
// reported, by the boundary that caught it or as its own recoverable error.
function isGenericHydrationError(error: unknown): boolean {
  return /#423\b|error while hydrating/i.test(String((error as Error)?.message ?? error));
}

function mountAll() {
  const site = readSite();
  initErrorReporting(site);

  document.querySelectorAll<HTMLElement>('[data-v2-component]').forEach((el) => {
    const name = el.dataset.v2Component as string;

    try {
      const tree = buildTree({
        component: name,
        props: el.dataset.v2Props ? JSON.parse(el.dataset.v2Props) : {},
        layoutProps: el.dataset.v2LayoutProps ? JSON.parse(el.dataset.v2LayoutProps) : {},
        site,
      });

      // Whether the server got there first. Both paths stay live permanently: server
      // rendering disabled, timed out or crashed all leave the mount empty, and that
      // has to keep working — it is the only thing standing between a broken renderer
      // and a broken page.
      if (el.firstChild) {
        let mismatchReported = false;
        const appleDataDetectors = watchAppleDataDetectors(el);

        hydrateRoot(el, tree, {
          onRecoverableError: (error) => {
            if (mismatchReported || isGenericHydrationError(error)) {
              console.error(error);
              return;
            }

            mismatchReported = true;
            reportError(error, {
              source: 'hydration',
              component: name,
              appleDataDetectors: appleDataDetectors(),
            });
          },
        });
      } else {
        createRoot(el).render(tree);
      }
    } catch (error) {
      console.error(`Failed to mount V2 component "${name}"`, error);
      reportError(error, { source: 'mount', component: name });
    }
  });
}

// Kicked off at module scope so it overlaps with DOMContentLoaded rather than queueing
// behind it, and deliberately not awaited. No name-addressed icon renders on first paint —
// the desktop panels sit inside a Popover.Panel and the mobile menu inside a
// <Transition show>, both unmounted until opened — so blocking the mount on it would
// cost every visitor a round trip for icons most of them never see. NamedIcon
// subscribes to the load and redraws if anyone opens a menu first.
loadIcons();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountAll);
} else {
  mountAll();
}
